import React, {useState, useRef, useEffect} from 'react';
import {
    LayoutDashboard,
    Settings,
    Share2,
    GitMerge,
    ChevronUp,
    Check,
    LogOut,
    X,
    Sparkles,
    Clock,
    ArrowRight,
} from 'lucide-react';
import {ViewState, Organization, User, UserOrganization} from '../types.ts';
import Avatar from "boring-avatars";

interface SidebarProps {
    currentView: ViewState;
    onChangeView: (view: ViewState) => void;
    isOpen: boolean;
    onClose: () => void;
    user: User;
    organizations: UserOrganization[];
    currentOrganizationId: string;
    onSwitchOrganization: (orgId: string) => void;
    onLogout: () => void;
    onUpgradePlan?: () => void;
    onNavigateToBilling?: () => void;
    /** Days left in a trial that's scheduled to cancel; null when not applicable. */
    cancelingTrialDaysLeft?: number | null;
}

export const Sidebar: React.FC<SidebarProps> = ({
                                                    currentView,
                                                    onChangeView,
                                                    isOpen,
                                                    onClose,
                                                    user,
                                                    organizations,
                                                    currentOrganizationId,
                                                    onSwitchOrganization,
                                                    onLogout,
                                                    onUpgradePlan,
                                                    onNavigateToBilling,
                                                    cancelingTrialDaysLeft
                                                }) => {
    const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const currentOrg = organizations.find(o => o.id === currentOrganizationId) || organizations[0];

    const menuItems = [
        {id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard'},
        {id: 'integrations', icon: Share2, label: 'Integrations'},
        {id: 'settings', icon: Settings, label: 'Settings'},
    ];

    const handleNavigation = (view: ViewState) => {
        onChangeView(view);
        onClose();
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOrgMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <>
            <aside
                className={`w-64 bg-[#f4f1e6] flex flex-col h-screen fixed left-0 top-0 border-r border-[#e4e0d2] z-50 transition-transform duration-300 ease-in-out ${
                    isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
                }`}
            >
                <div className="p-6 flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        <div
                            className="bg-[#22c55e] p-2 rounded-xl">
                            <GitMerge size={24} className="text-white"/>
                        </div>
                        <h1 className="text-xl font-bold tracking-tight text-[#0a0a0a] leading-none">Review<br/>Me
                        </h1>
                    </div>
                    <button
                        onClick={onClose}
                        className="md:hidden text-[#4f4f48] hover:text-[#0a0a0a] p-1"
                    >
                        <X size={24}/>
                    </button>
                </div>

                <nav className="flex-1 px-4 space-y-2 py-4 overflow-y-auto">
                    {menuItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => handleNavigation(item.id as ViewState)}
                            className={`w-full flex items-center gap-3 px-4 py-3 transition-all rounded-xl text-sm ${
                                currentView === item.id
                                    ? 'bg-[#dcfce7] text-[#15803d] font-semibold border-l-2 border-[#22c55e]'
                                    : 'text-[#4f4f48] font-medium hover:bg-[#ece8db] hover:text-[#0a0a0a]'
                            }`}
                        >
                            <item.icon size={20} strokeWidth={2.5}/>
                            <span>{item.label}</span>
                        </button>
                    ))}

                    {/* Upgrade to Pro button for free orgs */}
                    {currentOrg?.plan === 'free' && onUpgradePlan && (
                        <button
                            onClick={onUpgradePlan}
                            className="w-full flex items-center gap-3 px-4 py-3 mt-4 transition-all rounded-xl border border-[#e4e0d2] bg-[#f3e8ff] text-[#7e22ce] hover:bg-[#ece8db] font-medium text-sm"
                        >
                            <Sparkles size={20} strokeWidth={2.5} className="text-[#f59e0b]"/>
                            <span>Upgrade to Pro</span>
                        </button>
                    )}

                    {/* Trial scheduled to cancel — persistent nudge to resume before it ends */}
                    {cancelingTrialDaysLeft != null && onNavigateToBilling && (
                        <button
                            onClick={() => {
                                onNavigateToBilling();
                                onClose();
                            }}
                            className="w-full text-left mt-4 rounded-xl border border-[#fde68a] bg-[#fef3c7] p-3.5 transition-colors hover:bg-[#fdebb0] group"
                        >
                            <div className="flex items-center gap-2 mb-1.5">
                                <Clock size={16} strokeWidth={2.5} className="text-[#b45309] shrink-0"/>
                                <span className="text-sm font-bold text-[#92400e] leading-tight">
                                    {cancelingTrialDaysLeft === 0
                                        ? 'Trial ends today'
                                        : `${cancelingTrialDaysLeft} ${cancelingTrialDaysLeft === 1 ? 'day' : 'days'} of Pro left`}
                                </span>
                            </div>
                            <p className="text-xs text-[#a16207] leading-snug mb-2.5">
                                Your trial is set to cancel. Resume anytime before it ends to keep your rules running — you won't be charged until it's over.
                            </p>
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#b45309] group-hover:gap-1.5 transition-all">
                                Keep Pro
                                <ArrowRight size={13} strokeWidth={2.5}/>
                            </span>
                        </button>
                    )}
                </nav>

                {/* User / Org Switcher Footer */}
                <div className="p-4 border-t border-[#e4e0d2] mt-auto relative" ref={menuRef}>

                    {isOrgMenuOpen && (
                        <div
                            className="absolute bottom-full left-4 right-4 mb-2 bg-white rounded-xl border border-[#e4e0d2] shadow-soft overflow-hidden z-20">

                            {/* User Details Header */}
                            <div
                                className="p-4 border-b border-[#e4e0d2] bg-[#f3e8ff] flex items-center gap-3">
                                <Avatar name={user.email} variant={"beam"}/>
                                {/*<img src={user.avatarUrl} alt={user.full_name}*/}
                                {/*     className="w-10 h-10 rounded-full border-2 border-white shadow-sm bg-indigo-200"/>*/}
                                <div className="overflow-hidden">
                                    <p className="font-bold text-[#0a0a0a] text-sm truncate">{user.full_name}</p>
                                    <p className="text-xs text-[#4f4f48] truncate">{user.email}</p>
                                </div>
                            </div>

                            {/* Org List */}
                            <div
                                className="p-2 bg-[#f4f1e6] text-[10px] font-bold text-[#4f4f48] uppercase tracking-wider border-b border-[#e4e0d2]">
                                Switch Organization
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                                {organizations.map(org => (
                                    <button
                                        key={org.id}
                                        onClick={() => {
                                            onSwitchOrganization(org.id);
                                            setIsOrgMenuOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-3 hover:bg-[#ece8db] flex items-center justify-between group"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className={`font-semibold text-sm ${org.id === currentOrganizationId ? 'text-[#15803d]' : 'text-[#0a0a0a]'}`}>
                                                {org.name}
                                            </span>
                                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                                org.plan === 'pro'
                                                    ? 'bg-[#f3e8ff] text-[#7e22ce]'
                                                    : 'bg-[#ece8db] text-[#4f4f48]'
                                            }`}>
                                                {org.plan === 'pro' ? 'PRO' : 'FREE'}
                                            </span>
                                        </div>
                                        {org.id === currentOrganizationId &&
                                            <Check size={16} className="text-[#22c55e]"
                                                   strokeWidth={3}/>}
                                    </button>
                                ))}
                            </div>

                            {/* Actions */}
                            <div className="p-2 border-t border-[#e4e0d2] grid grid-cols-1 gap-1">
                                <button
                                    onClick={() => {
                                        handleNavigation('settings');
                                        setIsOrgMenuOpen(false);
                                    }}
                                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#4f4f48] hover:text-[#0a0a0a] hover:bg-[#ece8db] rounded-lg transition-colors w-full"
                                >
                                    <Settings size={16} strokeWidth={2.5}/> Settings
                                </button>
                                <div className="h-px bg-[#e4e0d2] my-1"></div>
                                <button
                                    onClick={onLogout}
                                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#be123c] hover:bg-[#ffe4e6] rounded-lg transition-colors w-full"
                                >
                                    <LogOut size={16} strokeWidth={2.5}/> Log Out
                                </button>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={() => setIsOrgMenuOpen(!isOrgMenuOpen)}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border transition-all cursor-pointer ${
                            isOrgMenuOpen ? 'bg-[#ece8db] border-[#e4e0d2]' : 'border-transparent hover:bg-[#ece8db] hover:border-[#e4e0d2]'
                        }`}
                    >
                        {/* User Avatar with Org Badge */}
                        <div className="relative shrink-0">
                            <Avatar name={user.email} variant={"beam"}/>
                            {/*<img src={user.avatarUrl} alt={user.full_name}*/}
                            {/*     className="w-10 h-10 rounded-full border-2 border-slate-900 shadow-sm bg-indigo-100"/>*/}
                            <div
                                className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#22c55e] rounded-md border border-[#e4e0d2] flex items-center justify-center text-[10px] font-bold text-white">
                                {currentOrg?.name.substring(0, 1).toUpperCase()}
                            </div>
                        </div>

                        <div className="overflow-hidden flex-1 text-left">
                            <p className="text-sm font-semibold text-[#0a0a0a] truncate leading-tight">{user.full_name}</p>
                            <p className="text-xs text-[#4f4f48] font-medium truncate leading-tight">{currentOrg?.name}</p>
                        </div>

                        <ChevronUp size={16}
                                   className={`text-[#4f4f48] transition-transform ${isOrgMenuOpen ? 'rotate-180' : ''}`}
                                   strokeWidth={3}/>
                    </button>
                </div>
            </aside>
        </>
    );
};
